import { AnyAction } from 'redux';
import { getBase64 } from '@mews/framework';
import { AsyncAction, CommanderThunkAction } from '@mews-commander/types';
import { SalesforceArticleResult } from '@mews-commander/data-access';
import { anonymizeUrl, createFetchAction, openInBrowserTab } from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { getCurrentEnterpriseName } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { isCurrentProfileAdmin } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { ExtendedTicketFormValues, TicketFormValues } from './types';
import { prefix } from './module';
import { ARTICLE_LIST_LIMIT, HELP_CENTER_EVENT } from './constants';

const ARTICLE_SEARCH = prefix('ARTICLE_SEARCH');
const TICKET_SUBMITTED = prefix('TICKET_SUBMITTED');

export const submitSupportTicket =
    (values: TicketFormValues): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories, appClockService }) => {
        const state = getState();

        const { title, description, files } = values;
        const enhancedDescription = `
        ${description}

        /// Additional information ///
        Current URL: ${window.location.href}
        Created UTC: ${appClockService.getAppNowUtc().value}
        EnterpriseId: ${getCurrentEnterpriseId()}
        EnterpriseName: ${getCurrentEnterpriseName(state)}
        Browser detail: ${window.navigator.userAgent}
        IsAdmin: ${isCurrentProfileAdmin(state)}
        `;

        const promise = repositories.salesforce
            // @ts-expect-error there are new required properties (category and priority) in this endpoint
            .createCase({ title, description: enhancedDescription })
            .then(async ({ id }) => {
                if (!files || files.length === 0) {
                    return;
                }

                const fileUploadPromises = files.map(async (file) => {
                    const { name, type } = file;
                    const base64 = await getBase64(file);
                    return repositories.salesforce.createCaseAttachment({
                        caseId: id,
                        attachment: { name, type, data: base64 },
                    });
                });

                return Promise.all(fileUploadPromises);
            });

        return dispatch(createFetchAction(TICKET_SUBMITTED, promise));
    };

export const searchArticles =
    (searchPhrase: string, limit = ARTICLE_LIST_LIMIT): CommanderThunkAction<AsyncAction<SalesforceArticleResult>> =>
    async (dispatch, getState, { repositories, loggerService }) => {
        const promise = repositories.salesforce.searchKnowledgeBase({ searchPhrase, limit }).then((payload) => {
            loggerService.log(HELP_CENTER_EVENT, 'search-articles', {
                searchPhrase,
                limit,
                resultCount: payload.articles?.length || 0,
            });
            return payload;
        });

        return dispatch(createFetchAction(ARTICLE_SEARCH, promise));
    };

export const fetchTrendingArticles =
    (): CommanderThunkAction<AsyncAction<SalesforceArticleResult>> =>
    async (dispatch, getState, { repositories }) => {
        const promise = repositories.salesforce.getTrendingKnowledgeBaseArticles({});

        return dispatch(createFetchAction(ARTICLE_SEARCH, promise));
    };

export const navigateToArticle =
    (url: string, id: string, context: 'SuggestedArticle' | 'KnowledgeBaseArticle'): CommanderThunkAction<AnyAction> =>
    (_dispatch, _getState, { gainsightService }) => {
        gainsightService.trackEvent('HelpArticleClicked', {
            context,
            articleUrl: url,
            articleId: id,
            url: anonymizeUrl(window.location.href),
        });
        return openInBrowserTab(url);
    };

export const submitExtendedSupportTicket =
    (values: ExtendedTicketFormValues): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories, appClockService }) => {
        const state = getState();

        const { title, reason, category, priority, description, files, collaborators } = values;
        const enhancedDescription = `
        ${description}

        /// Additional information ///
        Current URL: ${window.location.href}
        Created UTC: ${appClockService.getAppNowUtc().value}
        EnterpriseId: ${getCurrentEnterpriseId()}
        EnterpriseName: ${getCurrentEnterpriseName(state)}
        Browser detail: ${window.navigator.userAgent}
        IsAdmin: ${isCurrentProfileAdmin(state)}
        `;

        const promise = repositories.salesforce
            .createCase({ title, reason, category, priority, description: enhancedDescription, collaborators })
            .then(async ({ id }) => {
                if (!files || files.length === 0) {
                    return;
                }

                const fileUploadPromises = files.map(async (file) => {
                    const { name, type } = file;
                    const base64 = await getBase64(file);
                    return repositories.salesforce.createCaseAttachment({
                        caseId: id,
                        attachment: { name, type, data: base64 },
                    });
                });

                return Promise.all(fileUploadPromises);
            });

        return dispatch(createFetchAction(TICKET_SUBMITTED, promise));
    };
